import React, {useEffect, useRef, useState, createRef} from 'react';
import FFTChartMerge from '@user-components/FFTChartMerge';
import {Grid, Box, IconButton, TextField, Button, MenuItem, Typography, FormControlLabel, Checkbox} from '@material-ui/core';
import {CheckCircle, Cancel} from '@material-ui/icons';
import {makeStyles} from "@material-ui/core/styles";
import ColorHelper from '@helper/ColorHelper';
import SocketProvider from '@user-provider/SocketProvider';
import MeasureHelper from '@helper/MeasureHelper';
import TimeCount from '@user-pages/exercise/common/TimeCount';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import StopIcon from '@material-ui/icons/Stop';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import SettingsIcon from '@material-ui/icons/Settings';
import Axios from 'axios';
import {showSnackbar} from '@helper/SnackbarHelper';
import Autocomplete from '@material-ui/lab/Autocomplete';
import FrequencyEqualizer from './FrequencyEqualizer';
import FFTLineChart from './FFTLineChart';
import FFTLineChart3D from './FFTLineChart3D';

function DraggableBox({children, className, initialPos, onPosChange}) {
    const boxRef = useRef(null)
    const dragState = useRef({isDragging: false, startX: 0, startY: 0, origX: 0, origY: 0})
    const [pos, setPos] = useState(initialPos || {x: 0, y: 0})

    const handleMouseDown = (e) => {
        // canvas 위에서는 드래그 시작하지 않음 (EQ 조작용)
        if (e.target.tagName === 'CANVAS') return
        dragState.current = {
            isDragging: true,
            startX: e.clientX,
            startY: e.clientY,
            origX: pos.x,
            origY: pos.y
        }
        e.preventDefault()
    }

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!dragState.current.isDragging) return
            const dx = e.clientX - dragState.current.startX
            const dy = e.clientY - dragState.current.startY
            const newPos = {x: dragState.current.origX + dx, y: dragState.current.origY + dy}
            setPos(newPos)
        }
        const handleMouseUp = () => {
            if (dragState.current.isDragging && onPosChange) {
                onPosChange(pos)
            }
            dragState.current.isDragging = false
        }
        window.addEventListener('mousemove', handleMouseMove)
        window.addEventListener('mouseup', handleMouseUp)
        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
            window.removeEventListener('mouseup', handleMouseUp)
        }
    }, [pos])

    return (
        <Box
            ref={boxRef}
            className={className}
            style={{transform: `translate(${pos.x}px, ${pos.y}px)`, cursor: 'move'}}
            onMouseDown={handleMouseDown}
        >
            {children}
        </Box>
    )
}

function StepInput({label, value, onChange, step = 0.1, width = 100, min, max, disabled, classes}) {
    const intervalRef = useRef(null)
    const timeoutRef = useRef(null)
    const valueRef = useRef(value)
    valueRef.current = value

    const roundValue = (v) => Math.round(v * 100) / 100

    const applyDelta = (delta) => {
        const next = roundValue(parseFloat(valueRef.current) + delta)
        let clamped = min !== undefined ? Math.max(min, next) : next
        clamped = max !== undefined ? Math.min(max, clamped) : clamped
        onChange(clamped)
    }

    const startRepeat = (delta) => {
        stopRepeat()
        applyDelta(delta)
        timeoutRef.current = setTimeout(() => {
            intervalRef.current = setInterval(() => {
                applyDelta(delta)
            }, 80)
        }, 300)
    }

    const stopRepeat = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
        }
        if (intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }
    }

    useEffect(() => {
        return () => stopRepeat()
    }, [])

    return (
        <Box display="flex" alignItems="center">
            <TextField
                type="number"
                label={label}
                variant="outlined"
                size="small"
                value={value}
                onChange={(e) => {
                    let val = parseFloat(e.target.value) || 0
                    if (min !== undefined) val = Math.max(min, val)
                    if (max !== undefined) val = Math.min(max, val)
                    onChange(val)
                }}
                disabled={disabled}
                InputProps={{className: classes.input}}
                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                inputProps={{step, min, max}}
                style={{width}}
            />
            <Box display="flex" flexDirection="column" style={{marginLeft: 2}}>
                <IconButton
                    size="small"
                    className={classes.stepBtn}
                    disabled={disabled}
                    onMouseDown={() => !disabled && startRepeat(step)}
                    onMouseUp={stopRepeat}
                    onMouseLeave={stopRepeat}
                >
                    <ArrowDropUpIcon style={{fontSize: 20}}/>
                </IconButton>
                <IconButton
                    size="small"
                    className={classes.stepBtn}
                    disabled={disabled}
                    onMouseDown={() => !disabled && startRepeat(-step)}
                    onMouseUp={stopRepeat}
                    onMouseLeave={stopRepeat}
                >
                    <ArrowDropDownIcon style={{fontSize: 20}}/>
                </IconButton>
            </Box>
        </Box>
    )
}

const {measureTypeToCode} = MeasureHelper
const MeasureHzType = 'measureBand'

const SOURCE_DEVICE = 'device'
const SOURCE_FILE = 'file'
const SOURCE_PRESCRIPTION = 'prescription'

const STORAGE_KEY = 'measureBandLocalSettings'

const loadSettings = () => {
    try {
        const saved = localStorage.getItem(STORAGE_KEY)
        return saved ? JSON.parse(saved) : {}
    } catch { return {} }
}

const saveSettings = (settings) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    } catch {}
}



const MeasureBandLocal = () => {
    const classes = useStyles()
    const measureRef = useRef()
    const timeCountRef = useRef()

    const saved = useRef(loadSettings()).current

    const [source, setSource] = useState(saved.source || SOURCE_DEVICE)
    const [measureTime, setMeasureTime] = useState(saved.measureTime ?? 120)
    const [highValue, setHighValue] = useState(saved.highValue ?? 0)
    const [lowValue, setLowValue] = useState(saved.lowValue ?? 55)
    const [isRunning, setIsRunning] = useState(false)
    const [speed, setSpeed] = useState(saved.speed ?? 1)
    const [amplification, setAmplification] = useState(saved.amplification ?? 1.5)
    const [noiseFilter, setNoiseFilter] = useState(saved.noiseFilter ?? 'A')
    const [loop, setLoop] = useState(saved.loop ?? false)
    const loopRef = useRef(loop)
    const [chartMode, setChartMode] = useState(saved.chartMode || '3d')
    const lineChartRef = useRef()
    const lineChart3DRef = useRef()

    const [fileReady, setFileReady] = useState(false)
    const [fileName, setFileName] = useState('')
    const [deviceConnected, setDeviceConnected] = useState(false)
    const [resolution, setResolution] = useState(saved.resolution || 'normal')
    const [prescriptions, setPrescriptions] = useState([])
    const [selectedPrescriptionID, setSelectedPrescriptionID] = useState('')
    const [prescriptionDataReady, setPrescriptionDataReady] = useState(false)
    const [noiseProfileEnabled] = useState(false)
    const [noiseProfileName] = useState('')
    const [settingsOpen, setSettingsOpen] = useState(saved.settingsOpen ?? false)
    const [imagePopupOpen, setImagePopupOpen] = useState(false)

    const resolutionRef = useRef(resolution)
    // 노이즈 제거 EQ (좌/우)
    const eqCurveLRef = useRef(saved.eqCurveL && saved.eqCurveL.length === 30 ? saved.eqCurveL : new Array(30).fill(0))
    const eqCurveRRef = useRef(saved.eqCurveR && saved.eqCurveR.length === 30 ? saved.eqCurveR : new Array(30).fill(0))
    const eqRefL = useRef()
    const eqRefR = useRef()
    // 증폭 EQ (좌/우)
    const ampEqCurveLRef = useRef(saved.ampEqCurveL && saved.ampEqCurveL.length === 30 ? saved.ampEqCurveL : new Array(30).fill(1.0))
    const ampEqCurveRRef = useRef(saved.ampEqCurveR && saved.ampEqCurveR.length === 30 ? saved.ampEqCurveR : new Array(30).fill(1.0))
    const ampEqRefL = useRef()
    const ampEqRefR = useRef()
    useEffect(() => { resolutionRef.current = resolution }, [resolution])
    useEffect(() => { loopRef.current = loop }, [loop])
    const speedOptions = [0.2, 0.5, 0.7, 1, 1.2, 1.5, 1.7, 2]
    const noiseFilterOptions = [
        {value: 'A', label: 'A', desc: 'Spectral Smoothing — 3-bin 이동평균, 랜덤 노이즈 피크 완화, 신호 왜곡 최소'},
        {value: 'D', label: 'D', desc: 'Wiener Filter — SNR 추정 후 주파수별 감쇠, 가장 정밀'},
        {value: 'off', label: '없음', desc: '노이즈 제거 없음'},
    ]

    // 설정 변경 시 자동 저장
    useEffect(() => {
        saveSettings({source, measureTime, highValue, lowValue, speed, amplification, noiseFilter, resolution, noiseProfileEnabled, eqCurveL: eqCurveLRef.current, eqCurveR: eqCurveRRef.current, ampEqCurveL: ampEqCurveLRef.current, ampEqCurveR: ampEqCurveRRef.current, loop, chartMode})
    }, [source, measureTime, highValue, lowValue, speed, amplification, noiseFilter, resolution, noiseProfileEnabled, loop, chartMode])

    const isFileMode = source === SOURCE_FILE
    const isPrescriptionMode = source === SOURCE_PRESCRIPTION
    const canPlay = isFileMode ? fileReady : isPrescriptionMode ? prescriptionDataReady : deviceConnected

    useEffect(() => {
        if (!window.app) {
            window.app = {}
        }

        SocketProvider.connect(() => {}, window, MeasureHzType)


        SocketProvider.onMeasure = (data) => {
            if (measureRef.current) {
                measureRef.current.update(data)
            }
        }

        // 회차 모드면 목록 자동 로드
        if (source === SOURCE_PRESCRIPTION) {
            Axios.get('/api/manage/prescriptions-test/ids')
                .then(res => setPrescriptions(res.data.prescriptions || []))
                .catch(() => setPrescriptions([]))
        }

        // 기기 연결 상태 체크: C# 클라이언트에게 물어봄
        const checkDeviceStatus = async () => {
            try {
                if (window.BRHW?.isDeviceConnected) {
                    const connected = await window.BRHW.isDeviceConnected()
                    setDeviceConnected(!!connected)
                }
            } catch (e) {
                setDeviceConnected(false)
            }
        }
        checkDeviceStatus()
        const deviceCheckInterval = setInterval(checkDeviceStatus, 3000)

        // 새로고침 시 이미 측정 중이면 중지
        const stopIfMeasuring = async () => {
            try {
                if (window.BRHW?.isMeasuring) {
                    const measuring = await window.BRHW.isMeasuring()
                    if (measuring) {
                        if (window.BRHW?.stopMeasureFilePlay) window.BRHW.stopMeasureFilePlay()
                        if (window.BRHW?.deviceMeasureStop) window.BRHW.deviceMeasureStop()
                    }
                }
            } catch (e) {}
        }
        stopIfMeasuring()

        // C#에서 연결 상태가 변경되면 콜백으로도 받을 수 있음
        window.app.onDeviceStatusChanged = (connected) => {
            setDeviceConnected(!!connected)
        }

        const handleFFTData = (data) => {
            try {
                const parsed = typeof data === 'string' ? JSON.parse(data) : data
                // 노이즈 제거 EQ에 원본 FFT 라인 표시
                if (parsed.ch1FFT && parsed.ch2FFT) {
                    if (eqRefL.current && eqRefL.current.updateFFT) eqRefL.current.updateFFT(parsed.ch1FFT, null)
                    if (eqRefR.current && eqRefR.current.updateFFT) eqRefR.current.updateFFT(null, parsed.ch2FFT)
                }
                // 1) 노이즈 제거 EQ 적용 (채널별)
                if (parsed.ch1FFT) {
                    const curveL = eqCurveLRef.current
                    parsed.ch1FFT = parsed.ch1FFT.map((v, i) => i < curveL.length ? Math.max(0, v - curveL[i]) : v)
                }
                if (parsed.ch2FFT) {
                    const curveR = eqCurveRRef.current
                    parsed.ch2FFT = parsed.ch2FFT.map((v, i) => i < curveR.length ? Math.max(0, v - curveR[i]) : v)
                }
                // 2) 증폭 EQ 적용 (채널별, 노이즈 제거 후)
                if (parsed.ch1FFT) {
                    const ampL = ampEqCurveLRef.current
                    parsed.ch1FFT = parsed.ch1FFT.map((v, i) => i < ampL.length ? v * ampL[i] : v)
                }
                if (parsed.ch2FFT) {
                    const ampR = ampEqCurveRRef.current
                    parsed.ch2FFT = parsed.ch2FFT.map((v, i) => i < ampR.length ? v * ampR[i] : v)
                }
                if (measureRef.current) {
                    measureRef.current.update(parsed)
                }
                if (lineChartRef.current) {
                    lineChartRef.current.update(parsed)
                }
                if (lineChart3DRef.current) {
                    lineChart3DRef.current.addData(parsed.time, parsed.ch1FFT, parsed.ch2FFT)
                }

                // FFT 데이터의 time으로 타이머 업데이트
                if (parsed.time && timeCountRef.current) {
                    timeCountRef.current.update(parsed.time)
                }
            } catch (e) {
                console.error('[MeasureBandLocal] FFT 데이터 파싱 오류:', e)
            }
        }

        // 40bin FFT (일반/고해상도 모두 동일)
        window.app.onLocalFFTData = (data) => {
            handleFFTData(data)
        }

        window.app.onMeasureFileOpened = (name) => {
            console.log('[MeasureBandLocal] 파일 열림:', name)
            setFileReady(true)
            setFileName(name || '')
        }

        window.app.onMeasureFilePlayDone = () => {
            console.log('[MeasureBandLocal] 재생 완료, loop:', loopRef.current)
            if (loopRef.current) {
                // 루프: 차트 초기화 후 자동 재생
                if (measureRef.current) measureRef.current.clear()
                if (lineChart3DRef.current) lineChart3DRef.current.clearCanvas()
                if (lineChartRef.current) lineChartRef.current.clear()
                if (timeCountRef.current) {
                    timeCountRef.current.resetInterval()
                    timeCountRef.current.update(0)
                }
                try {
                    if (window.BRHW?.startMeasureFilePlay) {
                        window.BRHW.startMeasureFilePlay()
                    }
                } catch (e) {}
            } else {
                setIsRunning(false)
            }
        }

        window.app.onDeviceMeasureStop = () => {
            console.log('[MeasureBandLocal] 기기 측정 완료')
            if (timeCountRef.current) timeCountRef.current.pauseInterval()
            setIsRunning(false)
        }


        window.app.onNoiseProfileLoaded = (name) => {
            console.log('[MeasureBandLocal] 노이즈 프로파일 로드:', name)
        }

        return () => {
            if (measureRef.current) {
                measureRef.current.clear()
            }
            SocketProvider.measureStop(true).then()
            clearInterval(deviceCheckInterval)
            if (window.app) {
                window.app.onLocalFFTData = null

                window.app.onMeasureFileOpened = null
                window.app.onMeasureFilePlayDone = null
                window.app.onDeviceMeasureStop = null
                window.app.onNoiseProfileLoaded = null
                window.app.onDeviceStatusChanged = null
            }
        }
    }, [])

    const handleStart = async () => {
        if (isRunning) return
        if (measureRef.current) measureRef.current.clear()
        if (lineChart3DRef.current) lineChart3DRef.current.clearCanvas()
        if (lineChartRef.current) lineChartRef.current.clear()
        if (eqRefL.current && eqRefL.current.resetFFTAvg) eqRefL.current.resetFFTAvg()
        if (eqRefR.current && eqRefR.current.resetFFTAvg) eqRefR.current.resetFFTAvg()
        timeCountRef.current.resetInterval()
        timeCountRef.current.update(0)

        // 양쪽 모드 모두 설정값 전달
        try { if (window.BRHW?.setPlaySpeed) window.BRHW.setPlaySpeed(speed) } catch (e) {}
        try { if (window.BRHW?.setResolution) window.BRHW.setResolution(resolution) } catch (e) {}
        try { if (window.BRHW?.setAmplification) window.BRHW.setAmplification(parseFloat(amplification) || 1) } catch (e) {}
        try { if (window.BRHW?.setHighPass) window.BRHW.setHighPass(parseFloat(highValue) || 0) } catch (e) {}
        try { if (window.BRHW?.setLowPass) window.BRHW.setLowPass(parseFloat(lowValue) || 0) } catch (e) {}
        try { if (window.BRHW?.setNoiseFilter) window.BRHW.setNoiseFilter(noiseFilter === 'off' ? '' : noiseFilter) } catch (e) {}



        if (isFileMode) {
            // 파일 모드: C#에 재생 시작 요청
            try {
                if (window.BRHW?.startMeasureFilePlay) {
                    window.BRHW.startMeasureFilePlay()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 파일 재생 오류:', e)
            }
            setIsRunning(true)
        } else if (isPrescriptionMode) {
            // 회차 모드: raw 데이터를 서버에서 가져와서 C# 클라이언트에 전달
            try {
                const res = await Axios.get(`/api/manage/prescriptions-test/${selectedPrescriptionID}/raw`)
                if (res.data && res.data.data && window.BRHW?.loadRawData) {
                    window.BRHW.loadRawData(res.data.data)
                }
                if (window.BRHW?.startMeasureFilePlay) {
                    window.BRHW.startMeasureFilePlay()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 회차 재생 오류:', e)
            }
            setIsRunning(true)
        } else {
            // 기기 모드: C# 브릿지로 기기 측정 시작
            try {
                if (window.BRHW?.deviceMeasureStart) {
                    window.BRHW.deviceMeasureStart(measureTime, 'rest')
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 기기 측정 시작 오류:', e)
            }
            setIsRunning(true)
        }
    }

    const handleStop = async () => {
        if (!isRunning) return

        if (isFileMode || isPrescriptionMode) {
            try {
                if (window.BRHW?.stopMeasureFilePlay) {
                    window.BRHW.stopMeasureFilePlay()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 재생 중지 오류:', e)
            }
        } else {
            try {
                if (window.BRHW?.deviceMeasureStop) {
                    window.BRHW.deviceMeasureStop()
                }
            } catch (e) {
                console.error('[MeasureBandLocal] 기기 측정 중지 오류:', e)
            }
        }

        timeCountRef.current.pauseInterval()
        setIsRunning(false)
    }

    const handleSourceChange = async (newSource) => {
        if (isRunning) return
        setSource(newSource)
        setFileReady(false)
        setFileName('')
        setSelectedPrescriptionID('')
        setPrescriptionDataReady(false)
        try {
            if (window.BRHW?.setInputSource) {
                window.BRHW.setInputSource(newSource)
            }
        } catch (e) {
            console.error('[MeasureBandLocal] 입력소스 전송 오류:', e)
        }


        if (newSource === SOURCE_PRESCRIPTION) {
            try {
                const res = await Axios.get('/api/manage/prescriptions-test/ids')
                setPrescriptions(res.data.prescriptions || [])
            } catch (e) {
                console.error('[MeasureBandLocal] 회차 목록 조회 오류:', e)
                setPrescriptions([])
            }
        }
    }

    return (
        <Box style={{position: 'relative', overflowX: 'hidden'}}>
            {/* 상단 컨트롤 바 */}
            <Box className={classes.statusBar}>
                <Grid container alignItems="center" spacing={2} style={{height: '100%', padding: '0 16px'}}>
                    {/* 소스 선택 */}
                    <Grid item>
                        <TextField
                            select
                            label="입력소스"
                            variant="outlined"
                            size="small"
                            value={source}
                            onChange={(e) => handleSourceChange(e.target.value)}
                            disabled={isRunning}
                            InputProps={{className: classes.input}}
                            InputLabelProps={{className: classes.inputLabel, shrink: true}}
                            SelectProps={{classes: {icon: classes.selectIcon}}}
                            style={{width: 150}}
                        >
                            <MenuItem value={SOURCE_DEVICE}>기기에서 측정</MenuItem>
                            <MenuItem value={SOURCE_FILE}>파일에서 가져오기</MenuItem>
                            <MenuItem value={SOURCE_PRESCRIPTION}>회차 선택</MenuItem>
                        </TextField>
                    </Grid>

                    {/* 회차 선택 모드: 회차 목록 (검색 가능) */}
                    {isPrescriptionMode && (
                        <Grid item>
                            <Autocomplete
                                options={prescriptions}
                                getOptionLabel={(option) => `${option.id} - ${option.profileName || ''}`}
                                value={prescriptions.find(p => p.id === selectedPrescriptionID) || null}
                                onChange={async (e, newValue) => {
                                    const id = newValue ? newValue.id : ''
                                    setSelectedPrescriptionID(id)
                                    setPrescriptionDataReady(false)
                                    if (id) {
                                        try {
                                            const res = await Axios.get(`/api/manage/prescriptions-test/${id}/raw`)
                                            if (res.data && res.data.data) {
                                                if (window.BRHW?.loadRawData) {
                                                    window.BRHW.loadRawData(res.data.data)
                                                }
                                                setPrescriptionDataReady(true)
                                            } else {
                                                showSnackbar(`선택한 회차(${id})에 측정 정보가 없습니다\n뇌기능 측정 > EEG측정을 완료하면 볼 수 있습니다`, {variant: 'warning', autoHideDuration: 5000, style: {whiteSpace: 'pre-line'}})
                                            }
                                        } catch (ex) {
                                            showSnackbar(`선택한 회차(${id})에 측정 정보가 없습니다\n뇌기능 측정 > EEG측정을 완료하면 볼 수 있습니다`, {variant: 'warning', autoHideDuration: 5000, style: {whiteSpace: 'pre-line'}})
                                        }
                                    }
                                }}
                                disabled={isRunning}
                                size="small"
                                style={{width: 320}}
                                classes={{inputRoot: classes.autocompleteInput, popupIndicator: classes.selectIcon, clearIndicator: classes.selectIcon}}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="회차"
                                        variant="outlined"
                                        InputLabelProps={{className: classes.inputLabel, shrink: true}}
                                    />
                                )}
                            />
                        </Grid>
                    )}

                    {/* 측정방법 */}
                    <Grid item>
                        <TextField
                            select
                            label="측정방법"
                            variant="outlined"
                            size="small"
                            value={resolution}
                            onChange={(e) => {
                                setResolution(e.target.value)
                                try { if (window.BRHW?.setResolution) window.BRHW.setResolution(e.target.value) } catch (ex) {}
                            }}
                            disabled={isRunning}
                            InputProps={{className: classes.input}}
                            InputLabelProps={{className: classes.inputLabel, shrink: true}}
                            SelectProps={{classes: {icon: classes.selectIcon}}}
                            style={{width: 130}}
                        >
                            <MenuItem value="normal">일반 (1Hz)</MenuItem>
                            <MenuItem value="high">고해상도 (0.1Hz)</MenuItem>
                        </TextField>
                    </Grid>

                    {/* 기기 모드: 측정시간 */}
                    {source === SOURCE_DEVICE && (
                        <Grid item>
                            <TextField
                                type="number"
                                label="측정시간(초)"
                                variant="outlined"
                                size="small"
                                value={measureTime}
                                onChange={(e) => setMeasureTime(parseInt(e.target.value) || 0)}
                                disabled={isRunning}
                                InputProps={{className: classes.input}}
                                InputLabelProps={{className: classes.inputLabel}}
                                style={{width: 130}}
                            />
                        </Grid>
                    )}

                    {/* 파일 모드: 측정데이터 열기 */}
                    {isFileMode && (
                        <Grid item>
                            <Button
                                variant="contained"
                                className={classes.openFileBtn}
                                disabled={isRunning}
                                onClick={() => {
                                    try {
                                        if (window.BRHW?.openMeasureFile) {
                                            window.BRHW.openMeasureFile(speed, parseFloat(highValue) || 0, parseFloat(lowValue) || 0, parseFloat(amplification) || 0, noiseFilter)
                                        }
                                    } catch (e) {
                                        console.error('[MeasureBandLocal] 파일 열기 오류:', e)
                                    }
                                }}
                            >
                                {fileReady ? `📂 ${fileName}` : '측정데이터 열기'}
                            </Button>
                        </Grid>
                    )}

                    {/* 시작/중지 */}
                    <Grid item>
                        {!isRunning ? (
                            <IconButton
                                className={classes.startBtn}
                                onClick={handleStart}
                                disabled={!canPlay}
                            >
                                <PlayArrowIcon/>
                            </IconButton>
                        ) : (
                            <IconButton className={classes.stopBtn} onClick={handleStop}>
                                <StopIcon/>
                            </IconButton>
                        )}
                    </Grid>

                    {/* 타이머 */}
                    <Grid item style={{color: '#fff'}}>
                        <TimeCount ref={timeCountRef}/>
                    </Grid>

                    {/* 구분선 */}
                    <Grid item style={{paddingLeft: 24, paddingRight: 24}}>
                        <Box className={classes.divider}/>
                    </Grid>

                    {/* 측정이미지 버튼 */}
                    {isPrescriptionMode && selectedPrescriptionID && (
                        <Grid item>
                            <Button
                                variant="contained"
                                className={classes.openFileBtn}
                                size="small"
                                onClick={() => setImagePopupOpen(true)}
                            >
                                측정이미지
                            </Button>
                        </Grid>
                    )}

                    {/* 설정 버튼 */}
                    <Grid item>
                        <IconButton
                            className={classes.settingsBtn}
                            onClick={() => { const v = !settingsOpen; setSettingsOpen(v); saveSettings({...loadSettings(), settingsOpen: v}) }}
                        >
                            <SettingsIcon/>
                        </IconButton>
                    </Grid>

                    {/* 새로고침 */}
                    <Grid item style={{marginLeft: 'auto'}}>
                        <Button
                            variant="contained"
                            className={classes.refreshBtn}
                            onClick={() => window.location.reload()}
                            startIcon={<RefreshIcon/>}
                        >
                            새로고침
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {/* 카메라 조절 (+모양) */}
            <Box className={classes.cameraControls}>
                <Box style={{display: 'grid', gridTemplateColumns: '44px 44px 44px', gridTemplateRows: '38px 38px 38px', gap: 2}}>
                    <Box/>
                    <Button variant="outlined" size="small" className={classes.cameraBtn} onClick={() => { if (measureRef.current) measureRef.current.rotateCamera(0, -Math.PI/12) }}>▲</Button>
                    <Box/>
                    <Button variant="outlined" size="small" className={classes.cameraBtn} onClick={() => { if (measureRef.current) measureRef.current.rotateCamera(-Math.PI/12, 0) }}>◀</Button>
                    <Button variant="outlined" size="small" className={classes.cameraBtn} onClick={() => { if (measureRef.current) measureRef.current.resetCamera() }} style={{fontSize: 10}}>R</Button>
                    <Button variant="outlined" size="small" className={classes.cameraBtn} onClick={() => { if (measureRef.current) measureRef.current.rotateCamera(Math.PI/12, 0) }}>▶</Button>
                    <Box/>
                    <Button variant="outlined" size="small" className={classes.cameraBtn} onClick={() => { if (measureRef.current) measureRef.current.rotateCamera(0, Math.PI/12) }}>▼</Button>
                    <Box/>
                </Box>
            </Box>

            {/* 설정 팝업 */}
            {settingsOpen && (
                <DraggableBox
                    className={classes.settingsPopup}
                    initialPos={saved.settingsPos || {x: 0, y: 0}}
                    onPosChange={(pos) => saveSettings({...loadSettings(), settingsPos: pos})}
                >
                    <Box display="flex" alignItems="center" justifyContent="space-between" style={{marginBottom: 12}}>
                        <Typography style={{color: '#fff', fontSize: 14, fontWeight: 'bold'}}>설정</Typography>
                        <IconButton size="small" onClick={() => { setSettingsOpen(false); saveSettings({...loadSettings(), settingsOpen: false}) }} style={{color: '#fff'}}>
                            <Cancel style={{fontSize: 18}}/>
                        </IconButton>
                    </Box>

                    <Grid container spacing={2} alignItems="center">
                        {/* 하이패스 */}
                        <Grid item>
                            <StepInput
                                label="하이패스"
                                value={highValue}
                                step={resolution === 'high' ? 0.1 : 1}
                                min={0}
                                classes={classes}
                                onChange={(v) => {
                                    setHighValue(v)
                                    try { if (window.BRHW?.setHighPass) window.BRHW.setHighPass(parseFloat(v) || 0) } catch (ex) {}
                                }}
                            />
                        </Grid>
                        {/* 로우패스 */}
                        <Grid item>
                            <StepInput
                                label="로우패스"
                                value={lowValue}
                                step={resolution === 'high' ? 0.1 : 1}
                                min={0}
                                max={55}
                                classes={classes}
                                onChange={(v) => {
                                    setLowValue(v)
                                    try { if (window.BRHW?.setLowPass) window.BRHW.setLowPass(parseFloat(v) || 0) } catch (ex) {}
                                }}
                            />
                        </Grid>

                        {/* 노이즈필터 */}
                        <Grid item>
                            <TextField
                                select
                                label="노이즈필터"
                                variant="outlined"
                                size="small"
                                value={noiseFilter}
                                onChange={(e) => {
                                    setNoiseFilter(e.target.value)
                                    try { if (window.BRHW?.setNoiseFilter) window.BRHW.setNoiseFilter(e.target.value === 'off' ? '' : e.target.value) } catch (ex) {}
                                }}
                                InputProps={{className: classes.input}}
                                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                                SelectProps={{classes: {icon: classes.selectIcon}}}
                                style={{width: 100}}
                            >
                                {noiseFilterOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* 배속 */}
                        <Grid item>
                            <TextField
                                select
                                label="배속"
                                variant="outlined"
                                size="small"
                                value={speed}
                                onChange={(e) => {
                                    const s = parseFloat(e.target.value)
                                    setSpeed(s)
                                    try {
                                        if (window.BRHW?.setPlaySpeed) {
                                            window.BRHW.setPlaySpeed(s)
                                        }
                                    } catch (ex) {}
                                }}
                                InputProps={{className: classes.input}}
                                InputLabelProps={{className: classes.inputLabel, shrink: true}}
                                SelectProps={{classes: {icon: classes.selectIcon}}}
                                style={{width: 90}}
                            >
                                {speedOptions.map((s) => (
                                    <MenuItem key={s} value={s}>{s}x</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>

                    {/* 증폭 EQ 좌/우 */}
                    <Box display="flex" style={{marginTop: 16, gap: 12}}>
                        <FrequencyEqualizer
                            ref={ampEqRefL}
                            width={570}
                            height={260}
                            savedCurve={ampEqCurveLRef.current}
                            defaultValue={1.0}
                            min={0}
                            max={5}
                            label="증폭 - 좌"
                            showInfo
                            onChange={(curve) => {
                                ampEqCurveLRef.current = curve
                                saveSettings({source, measureTime, highValue, lowValue, speed, amplification, noiseFilter, resolution, noiseProfileEnabled, eqCurveL: eqCurveLRef.current, eqCurveR: eqCurveRRef.current, ampEqCurveL: curve, ampEqCurveR: ampEqCurveRRef.current, loop, chartMode})
                            }}
                        />
                        <FrequencyEqualizer
                            ref={ampEqRefR}
                            width={570}
                            height={260}
                            savedCurve={ampEqCurveRRef.current}
                            defaultValue={1.0}
                            min={0}
                            max={5}
                            label="증폭 - 우"
                            showInfo
                            onChange={(curve) => {
                                ampEqCurveRRef.current = curve
                                saveSettings({source, measureTime, highValue, lowValue, speed, amplification, noiseFilter, resolution, noiseProfileEnabled, eqCurveL: eqCurveLRef.current, eqCurveR: eqCurveRRef.current, ampEqCurveL: ampEqCurveLRef.current, ampEqCurveR: curve, loop, chartMode})
                            }}
                        />
                    </Box>

                    {/* 노이즈 제거 EQ 좌/우 */}
                    <Box display="flex" style={{marginTop: 12, gap: 12}}>
                        <FrequencyEqualizer
                            ref={eqRefL}
                            width={570}
                            height={200}
                            savedCurve={eqCurveLRef.current}
                            min={0}
                            max={5}
                            label="노이즈 - 좌"
                            showInfo
                            onChange={(curve) => {
                                eqCurveLRef.current = curve
                                saveSettings({source, measureTime, highValue, lowValue, speed, amplification, noiseFilter, resolution, noiseProfileEnabled, eqCurveL: curve, eqCurveR: eqCurveRRef.current, ampEqCurveL: ampEqCurveLRef.current, ampEqCurveR: ampEqCurveRRef.current, loop, chartMode})
                            }}
                        />
                        <FrequencyEqualizer
                            ref={eqRefR}
                            width={570}
                            height={200}
                            savedCurve={eqCurveRRef.current}
                            min={0}
                            max={5}
                            label="노이즈 - 우"
                            showInfo
                            onChange={(curve) => {
                                eqCurveRRef.current = curve
                                saveSettings({source, measureTime, highValue, lowValue, speed, amplification, noiseFilter, resolution, noiseProfileEnabled, eqCurveL: eqCurveLRef.current, eqCurveR: curve, ampEqCurveL: ampEqCurveLRef.current, ampEqCurveR: ampEqCurveRRef.current, loop, chartMode})
                            }}
                        />
                    </Box>
                </DraggableBox>
            )}

            {/* 측정이미지 팝업 */}
            {imagePopupOpen && selectedPrescriptionID && (
                <DraggableBox className={classes.imagePopup}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" style={{marginBottom: 8}}>
                        <Typography style={{color: '#333', fontSize: 16, fontWeight: 'bold'}}>
                            측정이미지 - 회차 {selectedPrescriptionID}
                        </Typography>
                        <IconButton size="small" onClick={() => setImagePopupOpen(false)} style={{color: '#333'}}>
                            <Cancel style={{fontSize: 20}}/>
                        </IconButton>
                    </Box>
                    <Box style={{textAlign: 'center'}}>
                        <img
                            src={`/api/prescription/${selectedPrescriptionID}/image/ch1?hzType=eeg&measureCode=C`}
                            alt="측정이미지"
                            style={{width: '100%', borderRadius: 4}}
                            onError={(e) => { e.target.style.display = 'none' }}
                        />
                    </Box>
                </DraggableBox>
            )}

            {/* 연결 상태 - 툴바 아래 왼쪽 */}
            {source === SOURCE_DEVICE && (
                <Box className={classes.deviceStatus}>
                    {deviceConnected ? (
                        <CheckCircle style={{color: '#4CAF50', fontSize: 16, marginRight: 4}}/>
                    ) : (
                        <Cancel style={{color: '#f44336', fontSize: 16, marginRight: 4}}/>
                    )}
                    <Typography style={{color: '#000', fontSize: 12}}>
                        {deviceConnected ? '연결됨' : '신호없음'}
                    </Typography>
                </Box>
            )}

            {/* 노이즈필터 설명 */}
            {noiseFilterOptions.find(o => o.value === noiseFilter) && (
                <Box className={classes.noiseDesc}>
                    {noiseFilterOptions.find(o => o.value === noiseFilter).desc}
                </Box>
            )}

            {/* 3D Bar 차트 */}
            {(chartMode === '3d' || chartMode === 'all') && (
                <Box className={'innerViewport'} style={{marginTop: 0, zoom: 1}}>
                    <LocalRealtimeMeasure ref={measureRef}/>
                </Box>
            )}

            {/* 3D Line 차트 */}
            {(chartMode === '3dline' || chartMode === 'all') && (
                <Box className={'innerViewport'} style={{marginTop: 0, zoom: 1}}>
                    <FFTLineChart3D ref={lineChart3DRef} height={700}/>
                </Box>
            )}

            {/* 2D Line 차트 */}
            {(chartMode === '2d' || chartMode === 'all') && (
                <Box style={{padding: '0 16px 16px'}}>
                    <FFTLineChart ref={lineChartRef} width={window.innerWidth - 40} height={350} maxHz={50}/>
                </Box>
            )}

        </Box>
    )
}


class LocalRealtimeMeasure extends React.Component {
    fftChartOptions = {
        graphWireFrame: false,
        graphWireFrameColor: 0x666666,
        hzSpacing: 10,
        hzCount: 60,
        cone: false,
        gridSize: 0.7,
        mvSize: 0.3,
        timeHzGrid: true,
        timeMvGrid: true,
        mvHzGrid: true,
        mvGuideLineXY: false,
        timeGuide: true,
        graphColors: ColorHelper.getColors(),
        height: 900,
        brainType: 'both',
        cameraType: 'side',
        noiseCode: 'A'
    }

    constructor(props) {
        super(props)
        this.fftChartRef = createRef(null)
    }

    update(data) {
        if (this.fftChartRef.current) {
            this.fftChartRef.current.addData(data.time, data.ch1FFT, data.ch2FFT)
        }
    }

    clear() {
        if (this.fftChartRef.current) {
            this.fftChartRef.current.clearCanvas()
        }
    }

    resetCamera() {
        if (this.fftChartRef.current && this.fftChartRef.current.cameraControls) {
            const cc = this.fftChartRef.current.cameraControls
            const side = this.fftChartRef.current.cameraPosition['side']
            cc.setLookAt(side[0], side[1], side[2], side[3], side[4], side[5], true)
            setTimeout(() => this.fftChartRef.current.renderChartOnce(), 100)
        }
    }

    rotateCamera(azimuth, polar) {
        if (this.fftChartRef.current && this.fftChartRef.current.cameraControls) {
            const cc = this.fftChartRef.current.cameraControls
            cc.rotate(azimuth, polar, true)
            setTimeout(() => this.fftChartRef.current.renderChartOnce(), 100)
        }
    }

    componentDidMount() {
        this.fftChartRef.current.initRender()
    }

    render() {
        const angle = Math.PI / 12 // 15도
        return (
            <Box>
                <Box padding={2}>
                    <Grid container style={{width: '100%', position: 'relative'}}>
                        <Grid item xs={12} style={{position: 'relative'}}>
                            <FFTChartMerge label={'Brain'} options={this.fftChartOptions} ref={this.fftChartRef}/>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        )
    }
}

const statusBarHeight = 60

const useStyles = makeStyles(() => ({
    statusBar: {
        position: 'absolute',
        top: 0,
        paddingTop: 20,
        paddingBottom: 10,
        left: 0,
        width: '100%',
        height: statusBarHeight + 20,
        backgroundColor: 'rgba(0,0,0,0.75)',
        zIndex: 99,
    },
    input: {
        color: '#fff',
        height: 36,
        '& input': {
            textAlign: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.4)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.7)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFC004',
        },
        '&.Mui-disabled': {
            color: 'rgba(255,255,255,0.7)',
        },
    },
    inputLabel: {
        color: 'rgba(255,255,255,0.7)',
        '&.Mui-focused': {
            color: '#FFC004',
        },
    },
    startBtn: {
        backgroundColor: '#FF0005',
        color: '#fff',
        width: 40,
        height: 40,
        '&:hover': {
            backgroundColor: '#C90009',
        },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(255,0,5,0.3)',
            color: 'rgba(255,255,255,0.4)',
        },
    },
    stopBtn: {
        backgroundColor: '#FF0005',
        color: '#fff',
        width: 40,
        height: 40,
        '&:hover': {
            backgroundColor: '#C90009',
        },
    },
    divider: {
        width: 1,
        height: 36,
        backgroundColor: 'rgba(255,255,255,0.3)',
    },
    openFileBtn: {
        backgroundColor: '#1976d2',
        color: '#fff',
        fontWeight: 'bold',
        height: 36,
        '&:hover': {
            backgroundColor: '#1565c0',
        },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(25,118,210,0.3)',
            color: 'rgba(255,255,255,0.4)',
        },
    },
    resetBtn: {
        color: '#fff',
        borderColor: 'rgba(255,255,255,0.5)',
        height: 36,
        '&:hover': {
            borderColor: '#fff',
        },
    },
    refreshBtn: {
        backgroundColor: '#757575',
        color: '#fff',
        fontWeight: 'bold',
        height: 36,
        '&:hover': {
            backgroundColor: '#616161',
        },
    },
    stepBtn: {
        color: '#fff',
        padding: 0,
        width: 20,
        height: 18,
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.15)',
        },
    },
    selectIcon: {
        color: 'rgba(255,255,255,0.7)',
    },
    autocompleteInput: {
        color: '#fff',
        height: 36,
        padding: '0 8px !important',
        display: 'flex',
        alignItems: 'center',
        marginTop: -4,
        '& .MuiAutocomplete-input': {
            padding: '0 4px !important',
            color: '#fff',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.4)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255,255,255,0.7)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#FFC004',
        },
    },
    deviceStatus: {
        position: 'absolute',
        top: statusBarHeight + 30,
        left: 8,
        display: 'flex',
        alignItems: 'center',
        zIndex: 100,
    },
    imagePopup: {
        position: 'absolute',
        top: statusBarHeight + 35,
        left: '5%',
        width: '90%',
        maxWidth: 1280,
        backgroundColor: '#fff',
        borderRadius: 8,
        padding: 16,
        zIndex: 100,
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
    },
    cameraControls: {
        position: 'absolute',
        top: 90,
        left: 50,
        display: 'flex',
        gap: 4,
        zIndex: 100,
    },
    cameraBtn: {
        color: '#333',
        borderColor: 'rgba(0,0,0,0.3)',
        minWidth: 44,
        height: 38,
        padding: '2px 0',
        fontSize: 18,
        '&:hover': {
            borderColor: '#333',
            backgroundColor: 'rgba(0,0,0,0.05)',
        },
    },
    settingsBtn: {
        color: '#fff',
        width: 40,
        height: 40,
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.15)',
        },
    },
    settingsPopup: {
        position: 'absolute',
        top: statusBarHeight + 35,
        left: 8,
        zIndex: 99,
        padding: 16,
        backgroundColor: 'rgba(0,0,0,0.9)',
        borderRadius: 8,
        width: 1200,
        border: '1px solid rgba(255,255,255,0.2)',
    },
    noiseDesc: {
        position: 'absolute',
        top: statusBarHeight + 30,
        right: 8,
        backgroundColor: 'rgba(0,0,0,0.7)',
        color: 'rgba(255,255,255,0.9)',
        fontSize: 16,
        padding: '6px 12px',
        borderRadius: 4,
        zIndex: 100,
    },
    speedBtn: {
        color: '#fff',
        borderColor: 'rgba(255,255,255,0.5)',
        height: 36,
        minWidth: 50,
        '&:hover': {
            borderColor: '#fff',
        },
    },
    speedBtnActive: {
        backgroundColor: '#FFC004',
        color: '#000',
        fontWeight: 'bold',
        height: 36,
        minWidth: 50,
        '&:hover': {
            backgroundColor: '#e0a800',
        },
    },
    loopBtn: {
        color: '#fff',
        borderColor: 'rgba(255,255,255,0.5)',
        height: 36,
        '&:hover': {
            borderColor: '#fff',
        },
    },
    loopBtnActive: {
        backgroundColor: '#4CAF50',
        color: '#fff',
        fontWeight: 'bold',
        height: 36,
        '&:hover': {
            backgroundColor: '#388E3C',
        },
    },
}))

export default MeasureBandLocal
